import {Link} from 'react-router-dom';
import { useState } from 'react';
import { Plus, Lightbulb, Pencil, Trash2, Link2, Users, Loader2 } from 'lucide-react';
import { deleteStakeholder } from '../../lib/mavisApi';
import type { MavisStakeholder, StakeholderRole, EngagementLevel } from '../../types/mavis';
import { STAKEHOLDER_ROLE_LABELS } from '../../types/mavis';
import MavisAddStakeholderModal, { type ContactOption } from './MavisAddStakeholderModal';

// ── Style maps ─────────────────────────────────────────────────────────────────

const ROLE_COLORS: Record<StakeholderRole, string> = {
  unknown: 'bg-app-canvas text-app-muted',
  champion:       'bg-forest-100 text-forest-700',
  economic_buyer: 'bg-indigo-100 text-indigo-700',
  technical_buyer:'bg-sky-100 text-sky-700',
  influencer:     'bg-violet-100 text-violet-700',
  partner:        'bg-teal-100 text-teal-700',
  detractor:      'bg-red-100 text-red-700',
  blocker:        'bg-orange-100 text-orange-700',
};

const ENGAGEMENT_COLORS: Record<EngagementLevel, string> = {
  strong:  'bg-forest-100 text-forest-700',
  moderate:'bg-amber-100 text-amber-700',
  weak:    'bg-red-100 text-red-700',
  unknown: 'bg-app-canvas text-app-muted',
};

const ENGAGEMENT_LABELS: Record<EngagementLevel, string> = {
  strong:  'Strong',
  moderate:'Moderate',
  weak:    'Weak',
  unknown: 'Unknown',
};

// ── Champion guidance card ─────────────────────────────────────────────────────

function ChampionGuidance() {
  return (
    <div className="bg-amber-50 border border-amber-200/60 rounded-2xl px-5 py-4 flex gap-3">
      <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-xs font-semibold text-amber-800 mb-0.5">What makes a strong Champion?</p>
        <p className="text-xs text-amber-700 leading-relaxed">
          A champion has influence, access to power, personal or professional motivation, understands
          the pain, supports your solution, and actively helps move the deal internally.
        </p>
      </div>
    </div>
  );
}

// ── Stakeholder card ───────────────────────────────────────────────────────────

interface CardProps {
  accountId?:string;
  stakeholder: MavisStakeholder;
  contacts:    ContactOption[];
  isDeleting:  boolean;
  isDeleteInFlight: boolean;
  onEdit:      () => void;
  onDeleteRequest:  () => void;
  onDeleteCancel:   () => void;
  onDeleteConfirm:  () => void;
}

function StakeholderCard({
  stakeholder: s, contacts,
  accountId,isDeleting, isDeleteInFlight, onEdit, onDeleteRequest, onDeleteCancel, onDeleteConfirm,
}: CardProps) {
  const linkedContact = s.contact_id
    ? contacts.find(c => c.id === s.contact_id)
    : null;

  return (
    <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-app-line flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${ROLE_COLORS[s.role_in_deal]}`}>
            {STAKEHOLDER_ROLE_LABELS[s.role_in_deal]}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${ENGAGEMENT_COLORS[s.engagement_level]}`}>
            {ENGAGEMENT_LABELS[s.engagement_level]}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {isDeleting ? (
            <>
              <span className="text-xs text-red-600 font-medium mr-1">Delete?</span>
              <button
                onClick={onDeleteCancel}
                disabled={isDeleteInFlight}
                className="px-2 py-1 text-xs text-app-muted hover:text-app-ink hover:bg-app-canvas rounded transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onDeleteConfirm}
                disabled={isDeleteInFlight}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-app-surface bg-red-500 hover:bg-red-600 rounded transition-colors disabled:opacity-60"
              >
                {isDeleteInFlight && <Loader2 className="w-3 h-3 animate-spin" />}
                Confirm
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEdit}
                className="p-1.5 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors"
                title="Edit stakeholder"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onDeleteRequest}
                className="p-1.5 text-app-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete stakeholder"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-2">
        <div>
          <p className="text-sm font-semibold text-app-ink">{s.name}</p>
          {s.title && (
            <p className="text-xs text-app-muted mt-0.5">{s.title}</p>
          )}
        </div>

        {/* Linked contact badge */}
        {linkedContact && (
          <div className="flex items-center gap-1.5">
            <Link2 className="w-3 h-3 text-forest-500 flex-shrink-0" />
            <span className="text-xs text-forest-700">
              {linkedContact.first_name} {linkedContact.last_name}
              {linkedContact.email && ` · ${linkedContact.email}`}
            </span>
          </div>
        )}

        {accountId && s.contact_id && <Link className="btn-secondary !text-xs" to={`/app/accounts/${accountId}?tab=execute&contactId=${s.contact_id}`}>Add to account Execute</Link>}
        {/* Notes */}
        {s.notes && (
          <p className="text-xs text-app-muted leading-relaxed line-clamp-3">{s.notes}</p>
        )}
      </div>
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────────

interface Props {
  accountId?:string;
  stakeholders:          MavisStakeholder[];
  contacts:              ContactOption[];
  opportunityId:         string;
  orgId:                 string;
  onStakeholderAdded:    (s: MavisStakeholder) => void;
  onStakeholderUpdated:  (s: MavisStakeholder) => void;
  onStakeholderDeleted:  (id: string) => void;
}

export default function MavisStakeholdersPanel({
  accountId,stakeholders, contacts, opportunityId, orgId,
  onStakeholderAdded, onStakeholderUpdated, onStakeholderDeleted,
}: Props) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<MavisStakeholder | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const openAdd  = () => { setEditing(null); setShowModal(true); };
  const openEdit = (s: MavisStakeholder) => { setEditing(s); setShowModal(true); };

  const handleSaved = (s: MavisStakeholder) => {
    if (editing) onStakeholderUpdated(s);
    else onStakeholderAdded(s);
    setShowModal(false);
    setEditing(null);
  };

  const handleDeleteConfirm = async (id: string) => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteStakeholder(id);
      onStakeholderDeleted(id);
      setDeletingId(null);
      setDeleting(false);
    } catch {
      setDeleteError('Failed to delete stakeholder. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-app-muted" />
          <h3 className="text-base font-semibold text-app-ink">
            Stakeholders
            {stakeholders.length > 0 && (
              <span className="ml-2 text-sm font-normal text-app-muted">
                ({stakeholders.length})
              </span>
            )}
          </h3>
        </div>
        <button onClick={openAdd} className="btn-primary !text-sm !py-2 !px-3.5">
          <Plus className="w-4 h-4 mr-1.5" />
          Add Stakeholder
        </button>
      </div>

      {/* Champion guidance */}
      <ChampionGuidance />

      {/* Stakeholder grid */}
      {stakeholders.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {stakeholders.map(s => (
            <StakeholderCard
              accountId={accountId}
              key={s.id}
              stakeholder={s}
              contacts={contacts}
              isDeleting={deletingId === s.id}
              isDeleteInFlight={deletingId === s.id && deleting}
              onEdit={() => openEdit(s)}
              onDeleteRequest={() => { setDeleteError(null); setDeletingId(s.id); }}
              onDeleteCancel={() => setDeletingId(null)}
              onDeleteConfirm={() => handleDeleteConfirm(s.id)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm py-14 text-center px-6">
          <Users className="w-10 h-10 text-app-muted mx-auto mb-4" />
          <p className="text-sm font-medium text-app-muted mb-1">No stakeholders mapped yet</p>
          <p className="text-xs text-app-muted max-w-sm mx-auto mb-5">
            Map the people who influence this deal — their roles, engagement levels, and
            whether they have access to power.
          </p>
          <button onClick={openAdd} className="btn-primary !text-sm !py-2 !px-4">
            <Plus className="w-4 h-4 mr-1.5" />
            Add First Stakeholder
          </button>
        </div>
      )}

      {/* Delete error */}
      {deleteError && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {deleteError}
        </div>
      )}

      {/* Add / Edit modal */}
      <MavisAddStakeholderModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        onSaved={handleSaved}
        opportunityId={opportunityId}
        orgId={orgId}
        contacts={contacts}
        stakeholder={editing}
      />
    </div>
  );
}
